/**
 * @fileoverview Which app a public help page was opened from.
 *
 * The iOS app opens the guides as `/guide?client=ios` and
 * `/student-guide?client=ios` (contract C5). Inside the iOS app, plans and
 * minutes are sold through Apple In-App Purchase, and App Review only allows
 * links to other purchase methods on some storefronts. So a page opened with
 * `client=ios` must not tell the reader to buy, upgrade or add minutes on the
 * web, and must not link to pricing or checkout.
 *
 * Guide content opts in per section:
 *   - `webOnly: true`  the section is hidden for the iOS app.
 *   - `iosOnly: true`  the section is shown only for the iOS app.
 *   - `ios: {...}`     fields (content, items, note, media) that replace the
 *                      web version for the iOS app. `media: null` drops a
 *                      screenshot of the web purchase surface.
 */

export const IOS_CLIENT = "ios";

/** The client named by a query string ("?client=ios"), or null for the web. */
export function clientFromSearch(search) {
  try {
    const value = new URLSearchParams(search || "").get("client");
    return value && value.toLowerCase() === IOS_CLIENT ? IOS_CLIENT : null;
  } catch {
    return null;
  }
}

/** Whether a query string says the page was opened from the iOS app. */
export function isIosClient(search) {
  return clientFromSearch(search) === IOS_CLIENT;
}

/**
 * A link to another public page that keeps the client: `to` with
 * `client=ios` added for the iOS app, and unchanged on the web. Without it one
 * tap inside the app's browser reloads the next page as the web version,
 * purchase copy and all. Use it only for a page that has an iOS mode; the
 * public chrome (PublicNavbar, PublicFooter) leaves every other page out.
 */
export function linkForClient(to, client) {
  if (client !== IOS_CLIENT) return to;
  const hashAt = to.indexOf("#");
  const path = hashAt === -1 ? to : to.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : to.slice(hashAt);
  const queryAt = path.indexOf("?");
  if (queryAt !== -1 && isIosClient(path.slice(queryAt))) return to;
  return `${path}${queryAt === -1 ? "?" : "&"}client=${IOS_CLIENT}${hash}`;
}

/**
 * The guide categories a client should see. Sections are filtered and their
 * client-specific fields applied; a category left with no sections is dropped
 * rather than shown as an empty heading.
 */
export function categoriesForClient(categories, client) {
  const ios = client === IOS_CLIENT;
  return categories
    .map((category) => ({
      ...category,
      sections: category.sections
        .filter((section) => (ios ? !section.webOnly : !section.iosOnly))
        .map((section) => {
          // Drop the authoring keys either way; apply the iOS overrides only for iOS.
          const { ios: overrides, webOnly: _webOnly, iosOnly: _iosOnly, ...rest } = section;
          if (!ios || !overrides) return rest;
          const merged = { ...rest, ...overrides };
          if (merged.media === null) delete merged.media;
          return merged;
        }),
    }))
    .filter((category) => category.sections.length > 0);
}
